import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Search, MapPin, Star, Filter, Droplet, Zap, Thermometer, Wrench, Sparkles, Clock, ArrowRight, Car, BadgeCheck, Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings, Grid, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContractorMap from "@/components/ContractorMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
  roofing: Home,
  "pest-control": Bug,
  remodeling: Hammer,
  landscaping: Trees,
  moving: Truck,
  "garage-door": DoorOpen,
  "appliance-repair": Settings,
};

const serviceColors = {
  plumbing: "bg-blue-100 text-blue-700",
  electrical: "bg-yellow-100 text-yellow-700",
  hvac: "bg-cyan-100 text-cyan-700",
  handyman: "bg-orange-100 text-orange-700",
  cleaning: "bg-purple-100 text-purple-700",
  mechanics: "bg-red-100 text-red-700",
  roofing: "bg-slate-100 text-slate-700",
  "pest-control": "bg-green-100 text-green-700",
  remodeling: "bg-amber-100 text-amber-700",
  landscaping: "bg-emerald-100 text-emerald-700",
  moving: "bg-indigo-100 text-indigo-700",
  "garage-door": "bg-gray-100 text-gray-700",
  "appliance-repair": "bg-pink-100 text-pink-700",
};

const BrowseWorkersPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [workers, setWorkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(searchParams.get("service") || "all");
  const [locationFilter, setLocationFilter] = useState(searchParams.get("location") || "");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "map"

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [workersRes, catRes] = await Promise.all([
          axios.get(`${API}/workers`, {
            params: {
              service: selectedService !== "all" ? selectedService : undefined,
              location: locationFilter || undefined
            }
          }),
          axios.get(`${API}/categories`)
        ]);
        setWorkers(workersRes.data);
        setCategories(catRes.data);
      } catch (error) {
        console.error("Error fetching workers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedService, locationFilter]);

  const handleServiceChange = (value) => {
    setSelectedService(value);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="browse-workers-page">
      <Navbar />

      {/* Header */}
      <section className="py-12 bg-[#f8f9fa] border-b border-[#dadce0]">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl font-medium text-[#202124] mb-2">
            {t("nav.browse")}
          </h1>
          <p className="text-[#5f6368]">
            {t("categories.subtitle")}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-[#dadce0] sticky top-0 bg-white z-10">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 text-[#5f6368]">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">{t("common.filter")}:</span>
            </div>
            
            <Select value={selectedService} onValueChange={handleServiceChange}>
              <SelectTrigger className="w-[180px] rounded-full border-[#dadce0]" data-testid="service-filter">
                <SelectValue placeholder={t("common.all")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#80868b]" />
              <input
                type="text"
                placeholder={t("forms.location")}
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="h-10 pl-10 pr-4 rounded-full border border-[#dadce0] text-sm focus:outline-none focus:ring-2 focus:ring-[#1a73e8] focus:border-transparent"
                data-testid="location-filter"
              />
            </div>

            <div className="ml-auto flex items-center gap-3">
              {/* View Toggle */}
              <div className="hidden md:flex items-center bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    viewMode === "grid" 
                      ? "bg-white text-blue-600 shadow-sm" 
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  data-testid="grid-view-btn"
                >
                  <Grid className="w-4 h-4" />
                  <span>Grid</span>
                </button>
                <button
                  onClick={() => setViewMode("map")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    viewMode === "map" 
                      ? "bg-white text-blue-600 shadow-sm" 
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  data-testid="map-view-btn"
                >
                  <Map className="w-4 h-4" />
                  <span>Map</span>
                </button>
              </div>
              
              {/* Mobile view toggle */}
              <button
                onClick={() => setViewMode(viewMode === "grid" ? "map" : "grid")}
                className="md:hidden flex items-center gap-1.5 px-3 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700"
              >
                {viewMode === "grid" ? (
                  <>
                    <Map className="w-4 h-4" />
                    <span>Map</span>
                  </>
                ) : (
                  <>
                    <Grid className="w-4 h-4" />
                    <span>Grid</span>
                  </>
                )}
              </button>
              
              <span className="text-sm text-[#5f6368]">
                {workers.length} {t("nav.browse").toLowerCase()}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Workers Grid */}
      <section className="section-spacing">
        <div className="container-custom">
          {loading ? (
            <div className="grid-workers">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="worker-card animate-pulse">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : workers.length === 0 ? (
            <div className="text-center py-20" data-testid="no-workers-message">
              <div className="w-16 h-16 rounded-full bg-[#f8f9fa] flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-[#5f6368]" />
              </div>
              <h3 className="text-xl font-medium text-[#202124] mb-2">{t("leads.noLeads")}</h3>
              <p className="text-[#5f6368] mb-6">{t("joinAsPro.title")}</p>
              <Button
                onClick={() => navigate("/join")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-6"
                data-testid="join-now-btn"
              >
                {t("nav.joinAsPro")}
              </Button>
            </div>
          ) : (
            <>
              {/* Map View */}
              {viewMode === "map" && (
                <div className="mb-8" data-testid="map-view-container">
                  <ContractorMap 
                    contractors={workers} 
                    selectedCity={locationFilter}
                    height="500px"
                    showSearch={true}
                  />
                </div>
              )}
              
              {/* Grid View */}
              {viewMode === "grid" && (
                <div className="grid-workers">
                  {workers.map((worker) => (
                    <Link 
                      key={worker.id} 
                      to={`/profile/${worker.id}`}
                      className="worker-card block hover:shadow-lg transition-shadow" 
                      data-testid={`worker-card-${worker.id}`}
                    >
                  {/* Worker Image Placeholder */}
                  <div className="h-48 bg-gradient-to-br from-[#e8f0fe] to-[#f8f9fa] flex items-center justify-center">
                    {worker.profile_photo ? (
                      <img src={worker.profile_photo} alt={worker.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-white shadow-md flex items-center justify-center">
                        <span className="text-3xl font-medium text-[#1a73e8]">
                          {worker.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-medium text-[#202124] flex items-center gap-2">
                          {worker.business_name || worker.name}
                          {worker.is_verified && (
                            <BadgeCheck className="w-4 h-4 text-[#1a73e8]" />
                          )}
                          {worker.is_featured && (
                            <span className="text-xs bg-[#1a73e8] text-white px-2 py-0.5 rounded-full">
                              Featured
                            </span>
                          )}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-[#5f6368] mt-1">
                          <MapPin className="w-4 h-4" />
                          {worker.location}
                        </div>
                      </div>
                      {worker.rating > 0 && (
                        <div className="badge-rating">
                          <Star className="w-3 h-3 fill-current" />
                          {worker.rating.toFixed(1)}
                        </div>
                      )}
                    </div>

                    {/* Services */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {worker.services.map((service) => {
                        const Icon = serviceIcons[service];
                        return (
                          <span
                            key={service}
                            className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${serviceColors[service]}`}
                          >
                            {Icon && <Icon className="w-3 h-3" />}
                            {service.charAt(0).toUpperCase() + service.slice(1)}
                          </span>
                        );
                      })}
                    </div>

                    <p className="text-sm text-[#5f6368] mb-4 line-clamp-2">
                      {worker.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-[#dadce0]">
                      <div className="flex items-center gap-4 text-sm text-[#5f6368]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {worker.experience_years} yrs exp
                        </span>
                        <span className="font-medium text-[#202124]">
                          ${worker.hourly_rate}{t("common.perHour")}
                        </span>
                      </div>
                      <span className="text-[#1a73e8] text-sm flex items-center gap-1">
                        {t("profile.aboutMe")} <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      {workers.length > 0 && (
        <section className="py-12 bg-[#f8f9fa]">
          <div className="container-custom text-center">
            <p className="text-[#5f6368] mb-4">{t("requestService.subtitle")}</p>
            <Button
              onClick={() => navigate("/request")}
              className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-6"
              data-testid="request-service-btn"
            >
              {t("requestService.title")}
            </Button>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BrowseWorkersPage;
